import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import type { CustomSqlSnippet, SqlSnippetEngineId } from '../types/sqlSnippetTypes';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';
import { showPersistWriteFailureToast } from '../../../components/ui/persistWriteFailureToast';

type SnippetsByEngine = Record<SqlSnippetEngineId, CustomSqlSnippet[]>;

type SqlSnippetsState = {
  readonly snippetsByEngine: SnippetsByEngine;

  readonly getSnippetsForEngine: (engineId: SqlSnippetEngineId) => readonly CustomSqlSnippet[];
  readonly addSnippet: (snippet: Omit<CustomSqlSnippet, 'id'> & { readonly id?: string }) => CustomSqlSnippet;
  readonly updateSnippet: (snippetId: string, updates: Partial<Omit<CustomSqlSnippet, 'id'>>) => void;
  readonly deleteSnippet: (snippetId: string) => void;
  readonly clearEngineSnippets: (engineId: SqlSnippetEngineId) => void;
  readonly clearAllSnippets: () => void;
};

const createEmptySnippetsByEngine = (): SnippetsByEngine => ({ mssql: [], pgsql: [], sqlite: [], mysql: [], oracle: [] });

const normalizeSnippet = (snippet: CustomSqlSnippet): CustomSqlSnippet => ({
  ...snippet,
  label: snippet.label.trim(),
  prefix: snippet.prefix.trim(),
  description: snippet.description.trim(),
});

const sortSnippets = (snippets: readonly CustomSqlSnippet[]): CustomSqlSnippet[] => {
  return [...snippets].sort((a, b) => {
    const aKey = `${a.prefix}|${a.label}`.toLowerCase();
    const bKey = `${b.prefix}|${b.label}`.toLowerCase();
    return aKey.localeCompare(bKey);
  });
};

const migrateSnippetsByEngine = (value: unknown): SnippetsByEngine => {
  if (!value || typeof value !== 'object') return createEmptySnippetsByEngine();

  const input = value as Partial<Record<string, unknown>>;
  const normalizeList = (engineId: SqlSnippetEngineId): CustomSqlSnippet[] => {
    const list = input[engineId];
    if (!Array.isArray(list)) return [];

    return list
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const record = item as Partial<CustomSqlSnippet>;
        if (typeof record.id !== 'string') return null;
        if (record.engineId !== 'mssql' && record.engineId !== 'pgsql' && record.engineId !== 'sqlite' && record.engineId !== 'mysql' && record.engineId !== 'oracle') return null;
        if (typeof record.label !== 'string') return null;
        if (typeof record.prefix !== 'string') return null;
        if (typeof record.description !== 'string') return null;
        if (typeof record.body !== 'string') return null;
        if (record.engineId !== engineId) return null;
        return normalizeSnippet(record as CustomSqlSnippet);
      })
      .filter((item): item is CustomSqlSnippet => item !== null);
  };

  return {
    mssql: sortSnippets(normalizeList('mssql')),
    pgsql: sortSnippets(normalizeList('pgsql')),
    sqlite: sortSnippets(normalizeList('sqlite')),
    mysql: sortSnippets(normalizeList('mysql')),
    oracle: sortSnippets(normalizeList('oracle')),
  };
};

export const useSqlSnippetsStore = create<SqlSnippetsState>()(
  persist(
    (set, get) => ({
      snippetsByEngine: createEmptySnippetsByEngine(),

      getSnippetsForEngine: (engineId) => get().snippetsByEngine[engineId] ?? [],

      addSnippet: (snippetInput) => {
        const snippet: CustomSqlSnippet = normalizeSnippet({
          id: snippetInput.id ?? uuidv4(),
          engineId: snippetInput.engineId,
          label: snippetInput.label,
          prefix: snippetInput.prefix,
          description: snippetInput.description,
          body: snippetInput.body,
        });

        set((state) => {
          const next: SnippetsByEngine = {
            ...state.snippetsByEngine,
            [snippet.engineId]: sortSnippets([snippet, ...(state.snippetsByEngine[snippet.engineId] ?? [])]),
          };
          return { snippetsByEngine: next };
        });

        return snippet;
      },

      updateSnippet: (snippetId, updates) => {
        set((state) => {
          const engines: readonly SqlSnippetEngineId[] = ['mssql', 'pgsql', 'sqlite', 'mysql', 'oracle'];

          let currentEngine: SqlSnippetEngineId | null = null;
          let currentSnippet: CustomSqlSnippet | null = null;

          for (const engineId of engines) {
            const found = state.snippetsByEngine[engineId]?.find((s) => s.id === snippetId);
            if (found) {
              currentEngine = engineId;
              currentSnippet = found;
              break;
            }
          }

          if (!currentEngine || !currentSnippet) return state;

          const nextSnippet = normalizeSnippet({
            ...currentSnippet,
            ...updates,
            id: currentSnippet.id,
          });

          const nextEngine = nextSnippet.engineId;

          if (nextEngine !== currentEngine) {
            const removedFromCurrent = (state.snippetsByEngine[currentEngine] ?? []).filter((s) => s.id !== snippetId);
            const addedToNext = sortSnippets([nextSnippet, ...(state.snippetsByEngine[nextEngine] ?? [])]);
            return {
              snippetsByEngine: {
                ...state.snippetsByEngine,
                [currentEngine]: removedFromCurrent,
                [nextEngine]: addedToNext,
              },
            };
          }

          const updated = (state.snippetsByEngine[currentEngine] ?? []).map((s) => (s.id === snippetId ? nextSnippet : s));
          return {
            snippetsByEngine: {
              ...state.snippetsByEngine,
              [currentEngine]: sortSnippets(updated),
            },
          };
        });
      },

      deleteSnippet: (snippetId) => {
        set((state) => {
          const next: SnippetsByEngine = {
            mssql: (state.snippetsByEngine.mssql ?? []).filter((s) => s.id !== snippetId),
            pgsql: (state.snippetsByEngine.pgsql ?? []).filter((s) => s.id !== snippetId),
            sqlite: (state.snippetsByEngine.sqlite ?? []).filter((s) => s.id !== snippetId),
            mysql: (state.snippetsByEngine.mysql ?? []).filter((s) => s.id !== snippetId),
            oracle: (state.snippetsByEngine.oracle ?? []).filter((s) => s.id !== snippetId),
          };
          return { snippetsByEngine: next };
        });
      },

      clearEngineSnippets: (engineId) => {
        set((state) => ({
          snippetsByEngine: {
            ...state.snippetsByEngine,
            [engineId]: [],
          },
        }));
      },

      clearAllSnippets: () => {
        set({ snippetsByEngine: createEmptySnippetsByEngine() });
      },
    }),
    {
      name: 'sql-studio-sql-snippets',
      storage: createGuardedJsonStorage({ onFailure: showPersistWriteFailureToast('your SQL snippets', { plural: true }) }),
      version: 1,
      partialize: (state) => ({ snippetsByEngine: state.snippetsByEngine }),
      migrate: (persistedState) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return { snippetsByEngine: createEmptySnippetsByEngine() };
        }

        const persisted = persistedState as { snippetsByEngine?: unknown };
        return { snippetsByEngine: migrateSnippetsByEngine(persisted.snippetsByEngine) };
      },
    }
  )
);
